import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  TOAST_TYPE,
  TOAST_TYPES,
  TOAST_DURATION,
  FORM_TOAST_CONTAINER_ID,
} from "./constants";

export {
  TOAST_TYPE,
  TOAST_TYPES,
  TOAST_DURATION,
  FORM_TOAST_CONTAINER_ID,
};

/**
 * Shared toast.
 *
 * Two pieces, and a component needs BOTH to show anything:
 *   1. `<Toaster />` — mounts the container. Render it once in the subtree that
 *      fires toasts.
 *   2. `showToast(message, { type })` — fires one.
 *
 * ⚠️ **WHY THIS EXISTS RATHER THAN A BARE `toast()` CALL.** `react-toastify`
 * renders nothing at all unless a `ToastContainer` is mounted somewhere in the
 * tree — and it fails SILENTLY: the call succeeds, no error, no toast. This repo
 * mounts containers ad-hoc in `components/Footer.jsx`, `components/Input`,
 * `pages/chemical-sourcing` and a few others, so whether a `toast()` is visible
 * depends on which unrelated component happens to be on the page. Pairing the
 * container with the trigger in one module removes that guesswork.
 *
 * ⚠️ Mounting more than one container makes toasts render twice. If a page already
 * has one (anything rendering the site Footer does), pass `containerId` or simply
 * don't render a second `<Toaster />` — see `Toaster`'s note.
 */

/**
 * Fire a toast.
 *
 * @param {string} message
 * @param {{ type?: string, autoClose?: number, containerId?: string }} [options]
 */
export function showToast(message, options = {}) {
  const { type = TOAST_TYPE.SUCCESS, autoClose, ...rest } = options;

  if (process.env.NODE_ENV !== "production" && !TOAST_TYPES.includes(type)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Toast] Unknown type "${type}". Use TOAST_TYPE.* — one of: ${TOAST_TYPES.join(", ")}.`,
    );
  }

  // Errors stay up longer than confirmations: a failure usually carries an
  // instruction, and the user has to read it before it disappears.
  const duration =
    autoClose ??
    (type === TOAST_TYPE.ERROR ? TOAST_DURATION.LONG : TOAST_DURATION.DEFAULT);

  const fn = toast[type] ?? toast;
  return fn(message, { autoClose: duration, ...rest });
}

/**
 * Mounts the toast container. Render once per subtree that calls `showToast`.
 *
 * ⚠️ Do NOT add a second one to a page that already mounts a container, or every
 * toast renders twice. `containerId` scopes it if that is unavoidable.
 *
 * Defaults chosen for form feedback: top-right, dismiss on click, pause on hover
 * (so a slow reader does not lose the message), and no progress bar for a calmer
 * confirmation.
 */
export function Toaster({
  position = "top-right",
  autoClose = TOAST_DURATION.DEFAULT,
  hideProgressBar = true,
  ...rest
}) {
  return (
    <ToastContainer
      position={position}
      autoClose={autoClose}
      hideProgressBar={hideProgressBar}
      closeOnClick
      pauseOnHover
      draggable={false}
      newestOnTop
      // Toastify's own aria-live region announces the message to screen readers.
      role="alert"
      {...rest}
    />
  );
}

export default showToast;
